import { useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { isAfter } from 'date-fns';

import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getKnmExternalParams, getKnmOnSaveButtonClick } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IKnmErknmFormAct } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormAct';
import { IKnmErknmFormResultDecision } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormResultDecision';
import { KnmKindCodesEnum } from 'APIPackage/DictionariesModule/enums/KnmKindCodesEnum';
import { IKnmErknmOrganizationTemplateSelectOption } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmOrganizationTemplateSelectOption';
import { IKnmErknmFormViolatedRequirementsNpa } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormViolatedRequirementsNpa';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IKnmErknmForm } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmForm';
import { parseDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';

import { ResultDecisionsItemContentActs } from './components/ResultDecisionsItemContentActs/ResultDecisionsItemContentActs';
import { ResultDecisionsItemContentExtract } from './components/ResultDecisionsItemContentExtract/ResultDecisionsItemContentExtract';
import { ResultDecisionsItemContentGeneral } from './components/ResultDecisionsItemContentGeneral/ResultDecisionsItemContentGeneral';
import { ResultDecisionsItemContentSubject } from './components/ResultDecisionsItemContentSubject/ResultDecisionsItemContentSubject';
import { ResultDecisionsItemContentViolations } from './components/ResultDecisionsItemContentViolations/ResultDecisionsItemContentViolations';
import { ResultDecisionsItemContentSignInfo } from './components/ResultDecisionsItemContentSignInfo/ResultDecisionsItemContentSignInfo';
import { ResultDecisionsItemContentSignButton } from './components/ResultDecisionsItemContentSignButton/ResultDecisionsItemContentSignButton';
import { ResultDecisionsItemContentIntroductions } from './components/ResultDecisionsItemContentIntroductions/ResultDecisionsItemContentIntroductions';
import { ResultDecisionsItemContentCancelInfo } from './components/ResultDecisionsItemContentCancelInfo/ResultDecisionsItemContentCancelInfo';

interface IErknmResultDecisionsItemPaperlessContentProps {
  resultDecisionId: string;
  name: string;
  acts: IKnmErknmFormAct[] | undefined;
  actsValue: IKnmErknmFormAct[] | undefined;
  watchOnly: boolean;
  value: IKnmErknmFormResultDecision;
  kindKnmCode: KnmKindCodesEnum | undefined;
  resultDecisions: IKnmErknmFormResultDecision[] | undefined;
  organizationsTemplatesSelectOptions: IKnmErknmOrganizationTemplateSelectOption[] | undefined;
  decisionIndex: number;
  addRequirementsNpa: (decisionIndex: number, fieldName: string) => void;
  actsRequirementsNpa: IKnmErknmFormViolatedRequirementsNpa[];
  formRef: IKnmFormRef | undefined;
  isPaperless: boolean;
  isLater4111ForDecision: boolean;
}

interface IErknmResultDecisionsItemPaperlessContentStoreProps {
  onSaveButtonClick: (() => void) | undefined;
  knmExternalParams?: Record<string, string>;
}

const ErknmResultDecisionsItemPaperlessContentWithoutStore = ({
  onSaveButtonClick,
  resultDecisionId,
  name,
  acts,
  watchOnly,
  value,
  resultDecisions,
  kindKnmCode,
  organizationsTemplatesSelectOptions,
  decisionIndex,
  addRequirementsNpa,
  actsRequirementsNpa,
  formRef,
  isPaperless,
  actsValue,
  isLater4111ForDecision,
  knmExternalParams,
}: IErknmResultDecisionsItemPaperlessContentProps & IErknmResultDecisionsItemPaperlessContentStoreProps) => {
  const { organizationTemplate, organization } = value;
  const isNewOrganizationTemplate = !!organizationTemplate && !organizationTemplate.value;
  const isDisabledField = watchOnly || !!value.document;

  const resultDecisionOrganizationsTemplatesSelectOptions:
    | IKnmErknmOrganizationTemplateSelectOption[]
    | undefined = organizationsTemplatesSelectOptions?.filter(organizationTemplateSelectOption => {
    return !(isNewOrganizationTemplate && organizationTemplateSelectOption.id === organization?.listId);
  });

  const violatedMandatoryRequirements = formRef?.current?.getFieldState(
    `${name}.violatedMandatoryRequirements` as keyof IKnmErknmForm,
  )?.value as IKnmErknmFormViolatedRequirementsNpa[] | undefined;

  const isExpandAllInPrescription = knmExternalParams?.expandAllInPrescription === 'true';

  const onChangeFixPlanDate = useCallback(() => {
    let maxDate = '';

    violatedMandatoryRequirements?.forEach(vmr => {
      vmr.violatedMandatory?.forEach(vm => {
        vm.violations?.forEach(v => {
          if (v.fixPlanDates?.length && v.fixPlanDates[0].value) {
            const currentDate = parseDate({
              date: v.fixPlanDates[0].value,
              parseFormat: DATE_FORMAT_LONG_FNS,
            });
            const maxDateFormated = parseDate({ date: maxDate || '', parseFormat: DATE_FORMAT_LONG_FNS });

            if (!maxDate || isAfter(currentDate || 0, maxDateFormated || 0)) {
              maxDate = v.fixPlanDates[0].value;
            }
          }
        });
      });
    });

    if (maxDate) {
      formRef?.current?.change(`${name}.injunction.dateResolved` as keyof IKnmErknmForm, maxDate);
    }
  }, [formRef, violatedMandatoryRequirements]);

  const selectedAct = useMemo(() => {
    return acts?.find(a => a.guid === value?.act?.value);
  }, [acts, value?.act]);

  return (
    <div>
      <ResultDecisionsItemContentActs
        resultDecisionId={resultDecisionId}
        name={name}
        acts={actsValue}
        watchOnly={isDisabledField || !!value.signed}
        resultDecisions={resultDecisions}
        formRef={formRef}
      />
      {!!value.act && (
        <>
          <ResultDecisionsItemContentExtract extract={value.document} />
          <ResultDecisionsItemContentGeneral
            formRef={formRef}
            isDisabledField={isDisabledField || !!value.signed}
            name={name}
            agreementStatus={value.agreementStatus}
            dateName={`${name}.dateDecision`}
            dateResolvedName={`${name}.injunction.dateResolved`}
            infoResolvedName={`${name}.injunction.note`}
            infoResolvedId={`${resultDecisionId}.injunction.note`}
            isHideInfoResolved={selectedAct?.isViolatedMandatoryRequirements?.id === 1}
            isKnm
          />
          <ResultDecisionsItemContentSubject
            id={resultDecisionId}
            kindKnmCode={kindKnmCode}
            organizationsTemplatesSelectOptions={resultDecisionOrganizationsTemplatesSelectOptions}
            name={name}
            value={value}
            isLater4111ForDecision={isLater4111ForDecision}
          />
          <ResultDecisionsItemContentViolations
            addRequirementsNpa={addRequirementsNpa}
            actsRequirementsNpa={actsRequirementsNpa}
            decisionIndex={decisionIndex}
            name={name}
            formRef={formRef}
            watchOnly={isDisabledField}
            clearWatchOnly={watchOnly}
            isPaperless={isPaperless}
            onChangeFixPlanDate={onChangeFixPlanDate}
            id={resultDecisionId}
            isKnm
            signed={value.signed}
            extractVersion={value.extractVersion}
            isExpandAllInPrescription={isExpandAllInPrescription}
          />
          <ResultDecisionsItemContentSignInfo
            isDisabledField={isDisabledField || !!value.signed}
            fioSignerFieldName={`${name}.fioSigner`}
            titleSignerFieldName={`${name}.titleSigner`}
            fioSignerId={`${resultDecisionId}.fioSigner`}
            titleSignerId={`${resultDecisionId}.titleSigner`}
            isKnm
            formRef={formRef}
            name={name}
          />
          {value.document && (
            <ResultDecisionsItemContentIntroductions id={resultDecisionId} name={name} formRef={formRef} />
          )}
          <ResultDecisionsItemContentSignButton
            isDisabledField={watchOnly}
            value={value}
            formRef={formRef}
            onSaveButtonClick={onSaveButtonClick}
            decisionIndex={decisionIndex}
            isResultDecision
            name={name}
          />
          <ResultDecisionsItemContentCancelInfo
            cancelDocument={value.cancelDocument}
            cancelCommentFieldName={`${name}.cancelComment`}
            fioSignerFieldName={`${name}.cancelFioSigner`}
            titleSignerFieldName={`${name}.cancelTitleSigner`}
            value={value}
            isPm
            isDecision={false}
            isKnm
            isCancelSigned={value.isCancelSigned}
          />
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state: IRootState): IErknmResultDecisionsItemPaperlessContentStoreProps => ({
  onSaveButtonClick: getKnmOnSaveButtonClick(state),
  knmExternalParams: getKnmExternalParams(state),
});

export const ErknmResultDecisionsItemPaperlessContent = connect(mapStateToProps)(
  ErknmResultDecisionsItemPaperlessContentWithoutStore,
);
